/**
 * SetariFlotaTab - Setări pentru flotă (trasee, motive închidere, configurări)
 * Structură cu sub-taburi pentru organizare
 */
import { memo, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Settings, Route, Plus, Trash2, AlertCircle, Pencil, Check, X, Cog, FileText, StopCircle, Wrench, Search, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Helper function for auth headers
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const SetariFlotaTab = memo(function SetariFlotaTab({
  routes,
  onAddRoute,
  onEditRoute,
  onDeleteRoute,
  closingReasons,
  onAddClosingReason,
  onEditClosingReason,
  onDeleteClosingReason,
  loading
}) {
  const [activeSubTab, setActiveSubTab] = useState('trasee');
  
  // State pentru trasee
  const [newRouteName, setNewRouteName] = useState('');
  const [newRouteScop, setNewRouteScop] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingRoute, setEditingRoute] = useState(null);
  const [editName, setEditName] = useState('');
  const [editScop, setEditScop] = useState('');

  // State pentru motive închidere
  const [newReasonId, setNewReasonId] = useState('');
  const [newReasonName, setNewReasonName] = useState('');
  const [editingReason, setEditingReason] = useState(null);
  const [editReasonId, setEditReasonId] = useState('');
  const [editReasonName, setEditReasonName] = useState('');
  const [deleteReasonConfirm, setDeleteReasonConfirm] = useState(null);

  // ========== Handlers Trasee ==========
  const handleAddRoute = async () => {
    if (!newRouteName.trim()) {
      toast.error('Introduceți numele traseului');
      return;
    }
    
    const success = await onAddRoute(newRouteName.trim(), newRouteScop.trim());
    if (success) {
      setNewRouteName('');
      setNewRouteScop('');
    }
  };

  const handleStartEditRoute = (route) => {
    const name = getRouteName(route);
    const scop = getRouteScop(route);
    setEditingRoute(name);
    setEditName(name);
    setEditScop(scop);
  };

  const handleCancelEditRoute = () => {
    setEditingRoute(null);
    setEditName('');
    setEditScop('');
  };

  const handleSaveEditRoute = async (originalName) => {
    if (!editName.trim()) {
      toast.error('Numele traseului este obligatoriu');
      return;
    }
    
    const success = await onEditRoute(originalName, editName.trim(), editScop.trim());
    if (success) {
      setEditingRoute(null);
      setEditName('');
      setEditScop('');
    }
  };

  const handleDeleteRoute = async (routeName) => {
    if (deleteConfirm === routeName) {
      await onDeleteRoute(routeName);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(routeName);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  const getRouteName = (route) => {
    if (typeof route === 'string') return route;
    return route?.name || '';
  };

  const getRouteScop = (route) => {
    if (typeof route === 'string') return '';
    return route?.scop || '';
  };

  // ========== Handlers Motive Închidere ==========
  const handleAddReason = async () => {
    if (!newReasonName.trim()) {
      toast.error('Introduceți numele motivului');
      return;
    }
    
    // Generează ID automat din nume dacă nu e specificat
    const id = newReasonId.trim() || newReasonName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    const success = await onAddClosingReason(id, newReasonName.trim());
    if (success) {
      setNewReasonId('');
      setNewReasonName('');
    }
  };

  const handleStartEditReason = (reason) => {
    setEditingReason(reason.id);
    setEditReasonId(reason.id);
    setEditReasonName(reason.name);
  };

  const handleCancelEditReason = () => {
    setEditingReason(null);
    setEditReasonId('');
    setEditReasonName('');
  };

  const handleSaveEditReason = async (originalId) => {
    if (!editReasonName.trim()) {
      toast.error('Numele motivului este obligatoriu');
      return;
    }
    
    const success = await onEditClosingReason(originalId, editReasonId.trim(), editReasonName.trim());
    if (success) {
      setEditingReason(null);
      setEditReasonId('');
      setEditReasonName('');
    }
  };

  const handleDeleteReason = async (reasonId) => {
    if (deleteReasonConfirm === reasonId) {
      await onDeleteClosingReason(reasonId);
      setDeleteReasonConfirm(null);
    } else {
      setDeleteReasonConfirm(reasonId);
      setTimeout(() => setDeleteReasonConfirm(null), 3000);
    }
  };

  // Definirea sub-taburilor
  const subTabs = [
    { id: 'trasee', label: 'Trasee', icon: Route },
    { id: 'motive', label: 'Motive Închidere', icon: StopCircle },
    { id: 'configurari', label: 'Configurări', icon: Cog },
    { id: 'pdf-settings', label: 'Setări PDF', icon: FileText },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Setări Flotă
          </CardTitle>
          <CardDescription>
            Configurează setările pentru gestionarea flotei
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Sub-taburi */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit">
        {subTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeSubTab === tab.id
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
              data-testid={`subtab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ========== Sub-tab Trasee ========== */}
      {activeSubTab === 'trasee' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Route className="w-5 h-5 text-blue-600" />
              Trasee
            </CardTitle>
            <CardDescription>
              Gestionează traseele disponibile pentru șoferi la deschiderea curselor
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Adaugă traseu nou */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="route-name" className="text-sm font-medium">
                    Nume traseu *
                  </Label>
                  <Input
                    id="route-name"
                    placeholder="Ex: Slobozia - București"
                    value={newRouteName}
                    onChange={(e) => setNewRouteName(e.target.value)}
                    className="mt-1"
                    data-testid="new-route-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="route-scop" className="text-sm font-medium">
                    Scop deplasare
                  </Label>
                  <Input
                    id="route-scop"
                    placeholder="Ex: Livrare colete conform aviz/factură"
                    value={newRouteScop}
                    onChange={(e) => setNewRouteScop(e.target.value)}
                    className="mt-1"
                    data-testid="new-route-scop-input"
                  />
                </div>
              </div>
              <Button 
                onClick={handleAddRoute} 
                disabled={loading || !newRouteName.trim()}
                data-testid="add-route-button"
              >
                <Plus className="w-4 h-4 mr-1" />
                Adaugă traseu
              </Button>
            </div>

            {/* Lista trasee existente */}
            <div className="border rounded-lg divide-y max-h-[350px] overflow-y-auto">
              {routes.length === 0 ? (
                <div className="p-4 text-center text-slate-500">
                  Nu există trasee definite
                </div>
              ) : (
                routes.map((route, index) => {
                  const routeName = getRouteName(route);
                  const routeScop = getRouteScop(route);
                  const isEditing = editingRoute === routeName;

                  return (
                    <div 
                      key={index} 
                      className={`p-3 ${isEditing ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                      data-testid={`route-item-${index}`}
                    >
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              placeholder="Nume traseu"
                              className="bg-white"
                              data-testid={`edit-route-name-${index}`}
                            />
                            <Input
                              value={editScop}
                              onChange={(e) => setEditScop(e.target.value)}
                              placeholder="Scop deplasare"
                              className="bg-white"
                              data-testid={`edit-route-scop-${index}`}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSaveEditRoute(routeName)}
                              disabled={loading}
                              data-testid={`save-route-${index}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Salvează
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEditRoute}
                              data-testid={`cancel-edit-${index}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Anulează
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium">{routeName}</div>
                            {routeScop && (
                              <div className="text-sm text-slate-500 mt-0.5">
                                Scop: {routeScop}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStartEditRoute(route)}
                              disabled={loading}
                              data-testid={`edit-route-${index}`}
                            >
                              <Pencil className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button
                              variant={deleteConfirm === routeName ? "destructive" : "ghost"}
                              size="sm"
                              onClick={() => handleDeleteRoute(routeName)}
                              disabled={loading}
                              data-testid={`delete-route-${index}`}
                            >
                              {deleteConfirm === routeName ? (
                                <>
                                  <AlertCircle className="w-4 h-4 mr-1" />
                                  Confirmă
                                </>
                              ) : (
                                <Trash2 className="w-4 h-4 text-red-500" />
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <p className="text-xs text-slate-500">
              Total: {routes.length} trasee • Scopul deplasării va apărea în Foaia de Parcurs
            </p>
          </CardContent>
        </Card>
      )}

      {/* ========== Sub-tab Motive Închidere ========== */}
      {activeSubTab === 'motive' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <StopCircle className="w-5 h-5 text-orange-600" />
              Motive Închidere Cursă
            </CardTitle>
            <CardDescription>
              Gestionează motivele disponibile pentru închiderea curselor de către șoferi
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Adaugă motiv nou */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="reason-name" className="text-sm font-medium">
                    Nume motiv *
                  </Label>
                  <Input
                    id="reason-name"
                    placeholder="Ex: Pauză de masă"
                    value={newReasonName}
                    onChange={(e) => setNewReasonName(e.target.value)}
                    className="mt-1"
                    data-testid="new-reason-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="reason-id" className="text-sm font-medium">
                    ID (opțional)
                  </Label>
                  <Input
                    id="reason-id"
                    placeholder="Ex: pauza_masa (generat automat)"
                    value={newReasonId}
                    onChange={(e) => setNewReasonId(e.target.value)}
                    className="mt-1"
                    data-testid="new-reason-id-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Se generează automat din nume dacă nu e specificat</p>
                </div>
              </div>
              <Button 
                onClick={handleAddReason} 
                disabled={loading || !newReasonName.trim()}
                data-testid="add-reason-button"
              >
                <Plus className="w-4 h-4 mr-1" />
                Adaugă motiv
              </Button>
            </div>

            {/* Lista motive existente */}
            <div className="border rounded-lg divide-y max-h-[350px] overflow-y-auto">
              {(!closingReasons || closingReasons.length === 0) ? (
                <div className="p-4 text-center text-slate-500">
                  Nu există motive definite
                </div>
              ) : (
                closingReasons.map((reason, index) => {
                  const isEditing = editingReason === reason.id;

                  return (
                    <div 
                      key={reason.id} 
                      className={`p-3 ${isEditing ? 'bg-orange-50' : 'hover:bg-slate-50'}`}
                      data-testid={`reason-item-${index}`}
                    >
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <Input
                              value={editReasonName}
                              onChange={(e) => setEditReasonName(e.target.value)}
                              placeholder="Nume motiv"
                              className="bg-white"
                              data-testid={`edit-reason-name-${index}`}
                            />
                            <Input
                              value={editReasonId}
                              onChange={(e) => setEditReasonId(e.target.value)}
                              placeholder="ID motiv"
                              className="bg-white"
                              data-testid={`edit-reason-id-${index}`}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSaveEditReason(reason.id)}
                              disabled={loading}
                              data-testid={`save-reason-${index}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Salvează
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEditReason}
                              data-testid={`cancel-edit-reason-${index}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Anulează
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium">{reason.name}</div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              ID: {reason.id}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStartEditReason(reason)}
                              disabled={loading}
                              data-testid={`edit-reason-${index}`}
                            >
                              <Pencil className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button
                              variant={deleteReasonConfirm === reason.id ? "destructive" : "ghost"}
                              size="sm"
                              onClick={() => handleDeleteReason(reason.id)}
                              disabled={loading || closingReasons.length <= 1}
                              data-testid={`delete-reason-${index}`}
                            >
                              {deleteReasonConfirm === reason.id ? (
                                <>
                                  <AlertCircle className="w-4 h-4 mr-1" />
                                  Confirmă
                                </>
                              ) : (
                                <Trash2 className="w-4 h-4 text-red-500" />
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <p className="text-xs text-slate-500">
              Total: {closingReasons?.length || 0} motive • Acestea apar în dropdown-ul de închidere cursă al șoferului
            </p>
          </CardContent>
        </Card>
      )}

      {/* ========== Sub-tab Configurări ========== */}
      {activeSubTab === 'configurari' && (
        <ConfigurariSubTab />
      )}

      {/* ========== Sub-tab Setări PDF ========== */}
      {activeSubTab === 'pdf-settings' && (
        <div className="space-y-6">
          <PDFSettingsSubTab />
          <CentralizatorPDFSettingsSubTab />
        </div>
      )}
    </div>
  );
});

// Sub-component for Configurări with diagnostic features
function ConfigurariSubTab() {
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);
  const [repairing, setRepairing] = useState(false);
  
  // State pentru activare documente programate
  const [activatingDocs, setActivatingDocs] = useState(false);
  const [activationResult, setActivationResult] = useState(null);

  const runDiagnostic = async () => {
    setLoadingDiagnostic(true);
    try {
      const response = await axios.get(`${API}/fleet/diagnostics`, getAuthHeaders());
      setDiagnosticResults(response.data);
      if (response.data.total_issues === 0) {
        toast.success('Nu au fost găsite probleme!');
      } else {
        toast.warning(`Găsite ${response.data.total_issues} probleme`);
      }
    } catch (error) {
      toast.error('Eroare la diagnosticare');
    } finally {
      setLoadingDiagnostic(false);
    }
  };

  const runRepair = async () => {
    setRepairing(true);
    try {
      const response = await axios.post(`${API}/fleet/repair`, {}, getAuthHeaders());
      toast.success(`Reparate ${response.data.repaired_count} mașini`);
      // Re-run diagnostic to show updated state
      await runDiagnostic();
    } catch (error) {
      toast.error('Eroare la reparare');
    } finally {
      setRepairing(false);
    }
  };

  // Activează documentele programate care au ajuns la data de activare
  const activatePendingDocuments = async () => {
    setActivatingDocs(true);
    setActivationResult(null);
    try {
      const response = await axios.post(`${API}/cars/activate-pending-documents`, {}, getAuthHeaders());
      setActivationResult(response.data);
      
      if (response.data.activated_count > 0) {
        toast.success(`${response.data.activated_count} documente activate cu succes!`);
      } else {
        toast.info('Niciun document de activat momentan');
      }
    } catch (error) {
      toast.error('Eroare la activarea documentelor');
      console.error(error);
    } finally {
      setActivatingDocs(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Cog className="w-5 h-5 text-slate-600" />
          Configurări & Diagnosticare
        </CardTitle>
        <CardDescription>
          Verifică și repară inconsistențe în date
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Diagnostic Section */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h3 className="font-semibold text-amber-900 flex items-center gap-2 mb-2">
            <Wrench className="w-5 h-5" />
            Diagnosticare Flotă
          </h3>
          <p className="text-sm text-amber-700 mb-4">
            Verifică mașinile marcate "În folosință" fără cursă activă și alte inconsistențe.
          </p>
          
          <div className="flex gap-3">
            <Button
              onClick={runDiagnostic}
              disabled={loadingDiagnostic}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              {loadingDiagnostic ? 'Se verifică...' : 'Rulează Diagnosticare'}
            </Button>
            
            {diagnosticResults && diagnosticResults.total_issues > 0 && (
              <Button
                onClick={runRepair}
                disabled={repairing}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                <Wrench className="w-4 h-4" />
                {repairing ? 'Se repară...' : `Repară ${diagnosticResults.total_issues} Probleme`}
              </Button>
            )}
          </div>
        </div>

        {/* Diagnostic Results */}
        {diagnosticResults && (
          <div className={`p-4 rounded-lg border ${
            diagnosticResults.total_issues === 0 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <h4 className={`font-semibold flex items-center gap-2 mb-3 ${
              diagnosticResults.total_issues === 0 ? 'text-green-800' : 'text-red-800'
            }`}>
              {diagnosticResults.total_issues === 0 ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Totul este în regulă!
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5" />
                  {diagnosticResults.total_issues} probleme găsite
                </>
              )}
            </h4>
            
            <div className="text-sm space-y-1 mb-3">
              <p>Mașini "În folosință": {diagnosticResults.in_use_cars_count}</p>
              <p>Curse active: {diagnosticResults.active_trips_count}</p>
            </div>

            {diagnosticResults.issues.length > 0 && (
              <div className="space-y-2 mt-4">
                <p className="text-sm font-medium text-red-800">Detalii probleme:</p>
                {diagnosticResults.issues.map((issue, idx) => (
                  <div key={idx} className="text-sm p-2 bg-white rounded border border-red-200">
                    <span className="font-mono text-red-700">{issue.license_plate}</span>
                    <span className="text-slate-600 ml-2">- {issue.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Activare Documente Programate Section */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5" />
            Activare Documente Programate
          </h3>
          <p className="text-sm text-blue-700 mb-4">
            Activează manual documentele viitoare care au ajuns la data de activare. 
            Aceasta actualizează automat datele de expirare ale mașinilor și elimină alertele rezolvate.
          </p>
          
          <Button
            onClick={activatePendingDocuments}
            disabled={activatingDocs}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            data-testid="activate-pending-docs-btn"
          >
            <CheckCircle className="w-4 h-4" />
            {activatingDocs ? 'Se activează...' : 'Activează Documente Programate'}
          </Button>

          {/* Activation Results */}
          {activationResult && (
            <div className={`mt-4 p-3 rounded-lg border ${
              activationResult.activated_count > 0 
                ? 'bg-green-50 border-green-200' 
                : 'bg-slate-50 border-slate-200'
            }`}>
              <p className={`font-medium flex items-center gap-2 ${
                activationResult.activated_count > 0 ? 'text-green-800' : 'text-slate-600'
              }`}>
                {activationResult.activated_count > 0 ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {activationResult.activated_count} documente activate cu succes
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4" />
                    Niciun document de activat
                  </>
                )}
              </p>
              
              {activationResult.activated && activationResult.activated.length > 0 && (
                <div className="mt-2 space-y-1">
                  {activationResult.activated.map((doc, idx) => (
                    <p key={idx} className="text-sm text-green-700">
                      ✓ {doc.car_name} - {doc.field_label}
                    </p>
                  ))}
                </div>
              )}
              
              {activationResult.errors_count > 0 && (
                <p className="mt-2 text-sm text-red-600">
                  ⚠ {activationResult.errors_count} erori la activare
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Sub-component for PDF Settings
function PDFSettingsSubTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/pdf-config`, getAuthHeaders());
      setSettings(response.data);
    } catch (error) {
      toast.error('Eroare la încărcarea setărilor PDF');
    } finally {
      setLoading(false);
    }
  };

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/pdf-config`, settings, getAuthHeaders());
      toast.success('Setări PDF salvate cu succes!');
    } catch (error) {
      toast.error('Eroare la salvarea setărilor');
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    try {
      const response = await axios.post(`${API}/settings/pdf-config/reset`, {}, getAuthHeaders());
      setSettings(response.data.config);
      toast.success('Setări resetate la valorile implicite');
    } catch (error) {
      toast.error('Eroare la resetarea setărilor');
    }
  };

  const updateColumnWidth = (columnId, newWidth) => {
    setSettings(prev => ({
      ...prev,
      columns: prev.columns.map(col => 
        col.id === columnId ? { ...col, width: parseInt(newWidth) } : col
      )
    }));
  };

  const updateColumnMaxChars = (columnId, newMaxChars) => {
    setSettings(prev => ({
      ...prev,
      columns: prev.columns.map(col => 
        col.id === columnId ? { ...col, maxChars: parseInt(newMaxChars) } : col
      )
    }));
  };

  const updateColumnEnabled = (columnId, enabled) => {
    setSettings(prev => ({
      ...prev,
      columns: prev.columns.map(col => 
        col.id === columnId ? { ...col, enabled } : col
      )
    }));
  };

  const getTotalWidth = () => {
    if (!settings?.columns) return 0;
    return settings.columns
      .filter(c => c.enabled !== false)
      .reduce((sum, col) => sum + (col.width || 0), 0);
  };

  if (loading || !settings) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-slate-500">Se încarcă setările...</p>
        </CardContent>
      </Card>
    );
  }

  const totalWidth = getTotalWidth();
  const maxWidth = 742; // A4 Landscape minus margins

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Setări PDF - Foi de Parcurs
        </CardTitle>
        <CardDescription>
          Configurează lățimea coloanelor și limita de caractere pentru exportul PDF
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Total Width Indicator */}
        <div className={`p-3 rounded-lg border ${totalWidth > maxWidth ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              Lățime totală coloane: <span className={totalWidth > maxWidth ? 'text-red-600' : 'text-green-600'}>{totalWidth}px</span>
            </span>
            <span className="text-sm text-slate-500">
              Disponibil: {maxWidth}px (A4 Landscape)
            </span>
          </div>
          {totalWidth > maxWidth && (
            <p className="text-xs text-red-600 mt-1">
              ⚠ Coloanele depășesc lățimea paginii cu {totalWidth - maxWidth}px. Redu lățimea unor coloane.
            </p>
          )}
        </div>

        {/* Columns Configuration */}
        <div className="space-y-4">
          <h3 className="font-medium text-slate-900">Coloane</h3>
          
          <div className="grid gap-3">
            {settings.columns.map((column) => (
              <div 
                key={column.id} 
                className={`p-3 border rounded-lg ${column.enabled !== false ? 'bg-white' : 'bg-slate-50 opacity-60'}`}
              >
                <div className="flex items-center gap-4">
                  {/* Enable/Disable */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={column.enabled !== false}
                      onChange={(e) => updateColumnEnabled(column.id, e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                  </label>

                  {/* Column Name */}
                  <div className="w-32">
                    <span className="font-medium text-sm">{column.name}</span>
                    <span className="text-xs text-slate-400 block">{column.id}</span>
                  </div>

                  {/* Width Slider */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-16">Lățime:</span>
                      <input
                        type="range"
                        min="30"
                        max="300"
                        value={column.width}
                        onChange={(e) => updateColumnWidth(column.id, e.target.value)}
                        className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        disabled={column.enabled === false}
                      />
                      <input
                        type="number"
                        min="30"
                        max="300"
                        value={column.width}
                        onChange={(e) => updateColumnWidth(column.id, e.target.value)}
                        className="w-16 px-2 py-1 text-sm border rounded"
                        disabled={column.enabled === false}
                      />
                      <span className="text-xs text-slate-400">px</span>
                    </div>
                  </div>

                  {/* Max Chars */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Max caractere:</span>
                    <input
                      type="number"
                      min="5"
                      max="100"
                      value={column.maxChars}
                      onChange={(e) => updateColumnMaxChars(column.id, e.target.value)}
                      className="w-16 px-2 py-1 text-sm border rounded"
                      disabled={column.enabled === false}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Font Settings */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-sm">Font date (pt)</Label>
            <Input
              type="number"
              min="4"
              max="12"
              value={settings.fontSize || 6}
              onChange={(e) => setSettings(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
            />
          </div>
          <div>
            <Label className="text-sm">Font header (pt)</Label>
            <Input
              type="number"
              min="5"
              max="14"
              value={settings.headerFontSize || 7}
              onChange={(e) => setSettings(prev => ({ ...prev, headerFontSize: parseInt(e.target.value) }))}
            />
          </div>
          <div>
            <Label className="text-sm">Înălțime rând (px)</Label>
            <Input
              type="number"
              min="6"
              max="20"
              value={settings.rowHeight || 9}
              onChange={(e) => setSettings(prev => ({ ...prev, rowHeight: parseInt(e.target.value) }))}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t">
          <Button
            onClick={saveSettings}
            disabled={saving || totalWidth > maxWidth}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? 'Se salvează...' : 'Salvează Setările'}
          </Button>
          
          <Button
            variant="outline"
            onClick={resetSettings}
          >
            Resetează la Implicit
          </Button>

          <div className="flex-1" />

          <p className="text-xs text-slate-500">
            Modificările se aplică imediat la următorul export PDF
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// Sub-component for Centralizator PDF Settings
function CentralizatorPDFSettingsSubTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/pdf-config-centralizator`, getAuthHeaders());
      setSettings(response.data);
    } catch (error) {
      toast.error('Eroare la încărcarea setărilor PDF Centralizator');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/pdf-config-centralizator`, settings, getAuthHeaders());
      toast.success('Setări PDF Centralizator salvate cu succes!');
    } catch (error) {
      toast.error('Eroare la salvarea setărilor');
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    try {
      const response = await axios.post(`${API}/settings/pdf-config-centralizator/reset`, {}, getAuthHeaders());
      setSettings(response.data.config);
      toast.success('Setări resetate la valorile implicite');
    } catch (error) {
      toast.error('Eroare la resetarea setărilor');
    }
  };

  const updateColumnWidth = (columnId, newWidth) => {
    setSettings(prev => ({
      ...prev,
      columns: prev.columns.map(col => 
        col.id === columnId ? { ...col, width: parseInt(newWidth) } : col
      )
    }));
  };

  const updateColumnMaxChars = (columnId, newMaxChars) => {
    setSettings(prev => ({
      ...prev,
      columns: prev.columns.map(col => 
        col.id === columnId ? { ...col, maxChars: parseInt(newMaxChars) } : col
      )
    }));
  };

  const updateColumnEnabled = (columnId, enabled) => {
    setSettings(prev => ({
      ...prev,
      columns: prev.columns.map(col => 
        col.id === columnId ? { ...col, enabled } : col
      )
    }));
  };

  const getTotalWidth = () => {
    if (!settings?.columns) return 0;
    return settings.columns
      .filter(c => c.enabled !== false)
      .reduce((sum, col) => sum + (col.width || 0), 0);
  };

  if (loading || !settings) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-slate-500">Se încarcă setările Centralizator...</p>
        </CardContent>
      </Card>
    );
  }

  const totalWidth = getTotalWidth();
  const maxWidth = 742; // A4 Landscape minus margins

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Setări PDF - Centralizator Lunar
        </CardTitle>
        <CardDescription>
          Configurează lățimea coloanelor și limita de caractere pentru exportul PDF Centralizator
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Total Width Indicator */}
        <div className={`p-3 rounded-lg border ${totalWidth > maxWidth ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              Lățime totală coloane: <span className={totalWidth > maxWidth ? 'text-red-600' : 'text-green-600'}>{totalWidth}px</span>
            </span>
            <span className="text-sm text-slate-500">
              Disponibil: {maxWidth}px (A4 Landscape)
            </span>
          </div>
          {totalWidth > maxWidth && (
            <p className="text-xs text-red-600 mt-1">
              Coloanele depășesc lățimea paginii cu {totalWidth - maxWidth}px. Redu lățimea unor coloane.
            </p>
          )}
        </div>

        {/* Columns Configuration */}
        <div className="space-y-4">
          <h3 className="font-medium text-slate-900">Coloane</h3>
          
          <div className="grid gap-3">
            {settings.columns && settings.columns.map((column) => (
              <div 
                key={column.id} 
                className={`p-3 border rounded-lg ${column.enabled !== false ? 'bg-white' : 'bg-slate-50 opacity-60'}`}
              >
                <div className="flex items-center gap-4">
                  {/* Enable/Disable */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={column.enabled !== false}
                      onChange={(e) => updateColumnEnabled(column.id, e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                  </label>

                  {/* Column Name */}
                  <div className="w-32">
                    <span className="font-medium text-sm">{column.name}</span>
                    <span className="text-xs text-slate-400 block">{column.id}</span>
                  </div>

                  {/* Width Slider */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-16">Lățime:</span>
                      <input
                        type="range"
                        min="20"
                        max="300"
                        value={column.width}
                        onChange={(e) => updateColumnWidth(column.id, e.target.value)}
                        className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        disabled={column.enabled === false}
                      />
                      <input
                        type="number"
                        min="20"
                        max="300"
                        value={column.width}
                        onChange={(e) => updateColumnWidth(column.id, e.target.value)}
                        className="w-16 px-2 py-1 text-sm border rounded"
                        disabled={column.enabled === false}
                      />
                      <span className="text-xs text-slate-400">px</span>
                    </div>
                  </div>

                  {/* Max Chars */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Max caractere:</span>
                    <input
                      type="number"
                      min="3"
                      max="100"
                      value={column.maxChars}
                      onChange={(e) => updateColumnMaxChars(column.id, e.target.value)}
                      className="w-16 px-2 py-1 text-sm border rounded"
                      disabled={column.enabled === false}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Font & Layout Settings */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Nume companie (opțional)</Label>
            <Input
              value={settings.companyName || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, companyName: e.target.value }))}
              placeholder="Ex: SC Transport SRL"
              data-testid="centralizator-pdf-company-name"
            />
          </div>
          <div>
            <Label className="text-sm">Adresă companie (opțional)</Label>
            <Input
              value={settings.companyAddress || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, companyAddress: e.target.value }))}
              placeholder="Ex: Str. Industriei nr. 10"
              data-testid="centralizator-pdf-company-address"
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div>
            <Label className="text-sm">Font date (pt)</Label>
            <Input
              type="number"
              min="4"
              max="12"
              value={settings.fontSize || 6}
              onChange={(e) => setSettings(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
            />
          </div>
          <div>
            <Label className="text-sm">Font header (pt)</Label>
            <Input
              type="number"
              min="5"
              max="14"
              value={settings.headerFontSize || 7}
              onChange={(e) => setSettings(prev => ({ ...prev, headerFontSize: parseInt(e.target.value) }))}
            />
          </div>
          <div>
            <Label className="text-sm">Font titlu (pt)</Label>
            <Input
              type="number"
              min="8"
              max="18"
              value={settings.titleFontSize || 12}
              onChange={(e) => setSettings(prev => ({ ...prev, titleFontSize: parseInt(e.target.value) }))}
            />
          </div>
          <div>
            <Label className="text-sm">Înălțime rând (px)</Label>
            <Input
              type="number"
              min="6"
              max="20"
              value={settings.rowHeight || 10}
              onChange={(e) => setSettings(prev => ({ ...prev, rowHeight: parseInt(e.target.value) }))}
            />
          </div>
        </div>

        {/* Header/Footer toggles */}
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showHeader !== false}
              onChange={(e) => setSettings(prev => ({ ...prev, showHeader: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300"
            />
            <span className="text-sm">Antet cu titlu și companie</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showFooter !== false}
              onChange={(e) => setSettings(prev => ({ ...prev, showFooter: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300"
            />
            <span className="text-sm">Subsol cu totaluri</span>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t">
          <Button
            onClick={saveSettings}
            disabled={saving || totalWidth > maxWidth}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="centralizator-pdf-save-btn"
          >
            {saving ? 'Se salvează...' : 'Salvează Setările'}
          </Button>

          <Button
            variant="outline"
            onClick={resetSettings}
            data-testid="centralizator-pdf-reset-btn"
          >
            Resetează la Implicit
          </Button>

          <div className="flex-1" />

          <p className="text-xs text-slate-500">
            Modificările se aplică imediat la următorul export PDF Centralizator
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default SetariFlotaTab;
